import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { BarChart3, RefreshCw, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import {
  quantWalkForward, quantWalkForwardRun, getErrorMessage,
} from '../../lib/api';

/**
 * WalkForwardTab — Latest run + history chart + re-run dialog.
 * Props:
 *   - strategies: string[] — lista strategie disponibili
 *   - defaultStrategy: string — strategia pre-selezionata
 */

const STABILITY_STYLES = {
  stable: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  'regime-dependent': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  unstable: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

// Mini line chart SVG per history mean_sharpe
const MiniLineChart = ({ data }) => {
  if (!data || data.length < 2) return <span className="text-xs text-muted-foreground italic">Dati insufficienti</span>;

  const values = data.map((d) => d.mean_sharpe);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 260;
  const H = 60;
  const pad = 6;

  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x},${y}`;
  });

  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {values.map((v, i) => {
        const x = pad + (i / (values.length - 1)) * (W - pad * 2);
        const y = H - pad - ((v - min) / range) * (H - pad * 2);
        return (
          <circle key={i} cx={x} cy={y} r="3" fill="hsl(var(--primary))" />
        );
      })}
      {/* Label ultimo valore */}
      {values.length > 0 && (
        <text
          x={W - pad + 4}
          y={H - pad - (((values[values.length - 1] - min) / range) * (H - pad * 2))}
          fontSize="9"
          fill="hsl(var(--muted-foreground))"
          dominantBaseline="middle"
        >
          {fmtNum(values[values.length - 1])}
        </text>
      )}
    </svg>
  );
};

// Dialog re-run walk-forward
const RunDialog = ({ open, onOpenChange, strategy, onSuccess }) => {
  const [asset, setAsset] = useState('');
  const [trainYears, setTrainYears] = useState('3');
  const [testYears, setTestYears] = useState('1');
  const [stepMonths, setStepMonths] = useState('6');
  const [refit, setRefit] = useState('true');
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (!strategy) return;
    setRunning(true);
    const tid = toast.loading('Walk-forward in corso...', {
      description: 'Può richiedere 30-120s a seconda dell\'universe.',
      duration: Infinity,
    });
    try {
      const r = await quantWalkForwardRun(strategy, {
        asset: asset || undefined,
        train_years: Number(trainYears),
        test_years: Number(testYears),
        step_months: Number(stepMonths),
        refit: refit === 'true',
      });
      if (r.data.ok) {
        toast.success('Walk-forward completato', {
          id: tid,
          description: `Durata: ${r.data.duration_sec?.toFixed(1) ?? '?'}s`,
          duration: 6000,
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error('Walk-forward fallito', {
          id: tid,
          description: r.data.error || 'Errore sconosciuto',
          duration: 8000,
        });
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore walk-forward'), { id: tid });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Re-run Walk-Forward · {strategy}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Asset (opzionale — default: tutti)</label>
            <input
              className="w-full h-9 rounded-md border bg-background px-3 text-sm font-mono"
              placeholder="es. SPY"
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Train (anni)</label>
              <Select value={trainYears} onValueChange={setTrainYears}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['1', '2', '3', '5', '7'].map((v) => (
                    <SelectItem key={v} value={v}>{v} anni</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Test (anni)</label>
              <Select value={testYears} onValueChange={setTestYears}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['0.5', '1', '2'].map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Step (mesi)</label>
              <Select value={stepMonths} onValueChange={setStepMonths}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['3', '6', '12'].map((v) => (
                    <SelectItem key={v} value={v}>{v}m</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Refit parametri</label>
            <Select value={refit} onValueChange={setRefit}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Sì — refit su ogni fold</SelectItem>
                <SelectItem value="false">No — parametri fissi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            Annulla
          </Button>
          <Button onClick={handleRun} disabled={running}>
            {running ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> In corso...</>
            ) : (
              <><Play className="w-3.5 h-3.5 mr-1" /> Avvia</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WalkForwardTab = ({ strategies = [], defaultStrategy = null }) => {
  const [selectedStrategy, setSelectedStrategy] = useState(defaultStrategy || strategies[0] || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);

  const fetchWF = useCallback(async () => {
    if (!selectedStrategy) return;
    setLoading(true);
    setError(null);
    try {
      const r = await quantWalkForward(selectedStrategy);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setData(null);
      } else {
        setError(getErrorMessage(e, 'Errore walk-forward'));
      }
    } finally {
      setLoading(false);
    }
  }, [selectedStrategy]);

  useEffect(() => { fetchWF(); }, [fetchWF]);

  // Sync quando cambia defaultStrategy dal parent
  useEffect(() => {
    if (defaultStrategy && defaultStrategy !== selectedStrategy) {
      setSelectedStrategy(defaultStrategy);
    }
  }, [defaultStrategy]); // eslint-disable-line react-hooks/exhaustive-deps

  const latest = data?.latest_run;
  const folds = latest?.folds || [];
  const aggregate = latest?.aggregate || {};
  const history = (data?.history || []).slice(-6); // ultimi 6 run
  const stability = aggregate?.stability_label || 'insufficient_data';

  return (
    <div className="space-y-4">
      {/* Selector + Re-run */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-48">
          <Select value={selectedStrategy} onValueChange={setSelectedStrategy}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Scegli strategia" />
            </SelectTrigger>
            <SelectContent>
              {strategies.map((s) => (
                <SelectItem key={s} value={s}>
                  <span className="font-mono text-xs">{s}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={fetchWF} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          size="sm"
          className="h-9"
          onClick={() => setRunDialogOpen(true)}
          disabled={!selectedStrategy}
        >
          <Play className="w-3.5 h-3.5 mr-1.5" />
          Re-run ora
        </Button>
      </div>

      {error && (
        <div className="text-xs text-red-500 bg-red-50 dark:bg-red-950/20 rounded px-3 py-2">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="h-64 bg-muted/40 animate-pulse rounded-lg" />
      ) : !data || !latest ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Nessun walk-forward disponibile per <code className="font-mono">{selectedStrategy}</code>.
            Avvia il primo run con il bottone sopra.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Latest run card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Ultimo run · {folds.length} fold{folds.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-2">
                  {/* Stability badge */}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${STABILITY_STYLES[stability] || 'bg-muted text-muted-foreground'}`}>
                    {stability.replace(/-/g, ' ')}
                  </span>
                  {aggregate.refit_used != null && (
                    <Badge variant="outline" className="text-[10px]">
                      refit: {aggregate.refit_used ? 'sì' : 'no'}
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {/* Aggregate KPI */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Mean Sharpe</p>
                  <p className="text-lg font-bold tabular-nums">{fmtNum(aggregate.mean_sharpe)}</p>
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Std Sharpe</p>
                  <p className="text-lg font-bold tabular-nums">{fmtNum(aggregate.std_sharpe)}</p>
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Folds totali</p>
                  <p className="text-lg font-bold tabular-nums">{folds.length}</p>
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Trade totali</p>
                  <p className="text-lg font-bold tabular-nums">
                    {folds.reduce((acc, f) => acc + (f.n_trades || 0), 0)}
                  </p>
                </div>
              </div>

              {/* Folds table */}
              {folds.length > 0 && (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Fold</TableHead>
                        <TableHead className="text-xs">Train</TableHead>
                        <TableHead className="text-xs">Test</TableHead>
                        <TableHead className="text-right text-xs">Sharpe</TableHead>
                        <TableHead className="text-right text-xs">Trade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {folds.map((f, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{i + 1}</TableCell>
                          <TableCell className="text-xs font-mono whitespace-nowrap">
                            {f.train_window || '—'}
                          </TableCell>
                          <TableCell className="text-xs font-mono whitespace-nowrap">
                            {f.test_window || '—'}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums text-sm font-medium ${
                            f.sharpe == null ? 'text-muted-foreground'
                              : Number(f.sharpe) >= 1 ? 'text-emerald-600 dark:text-emerald-400'
                              : Number(f.sharpe) >= 0 ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {fmtNum(f.sharpe)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {f.n_trades ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* History chart */}
          {history.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Storia run (ultimi {history.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <MiniLineChart data={history} />
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground font-mono">
                  {history.map((h, i) => (
                    <div key={i} className="text-center">
                      <div>{h.run_date ? new Date(h.run_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : `#${i + 1}`}</div>
                      <div className="font-semibold text-foreground">{fmtNum(h.mean_sharpe)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Re-run dialog */}
      <RunDialog
        open={runDialogOpen}
        onOpenChange={setRunDialogOpen}
        strategy={selectedStrategy}
        onSuccess={fetchWF}
      />
    </div>
  );
};

export default WalkForwardTab;
